import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { calctime } from '@/utils/common';

import FullScreenIcon from '../../icon/fullScreen';
import PauseIcon from '../../icon/pause';
import PlayIcon from '../../icon/play';
import { player } from '../../utils/playerClass';

interface ControllerBarProps {
  url: string;
  className?: string;
  videoRef?: React.RefObject<HTMLVideoElement | HTMLAudioElement>;
}

const ControllerBar: React.FC<ControllerBarProps> = memo(
  ({ url, className, videoRef }) => {
    const [playerStatus, setPlayerStatus] = useState<'play' | 'pause'>('pause');
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const progressRef = useRef<HTMLDivElement>(null);

    const [progressPercentage, setProgressPercentage] = useState(0);
    const isPlayerInit = useRef(false);

    useEffect(() => {
      const percentage = duration ? (currentTime / duration) * 100 : 0;
      setProgressPercentage(percentage);
    }, [currentTime, duration]);

    useEffect(() => {
      player.src = url;

      player.onTimeUpdate = (time: number) => {
        if (!isDragging) {
          setPlayerStatus('play');
          setCurrentTime(time / 1000);
        }
      };

      player.onLoadedMetadata = () => {
        setDuration(player.duration);
        isPlayerInit.current = true;
      };

      player.onPause = () => {
        setPlayerStatus('pause');
      };

      return () => {
        player.destroy();
      };
    }, [url]);

    const handlePlayPause = useCallback(() => {
      if (!isPlayerInit.current) return;

      if (playerStatus === 'pause') {
        player.play();
      } else {
        player.pause();
      }
    }, [playerStatus]);

    const calculateProgress = useCallback((clientX: number) => {
      if (!progressRef.current) return 0;

      const rect = progressRef.current.getBoundingClientRect();
      let percentage = ((clientX - rect.left) / rect.width) * 100;
      return Math.max(0, Math.min(100, percentage));
    }, []);

    const updateProgress = useCallback(
      (percentage: number) => {
        const newTime = (duration * percentage) / 100;
        setProgressPercentage(percentage);

        if (!isDragging) {
          player.currentTime = newTime;
        }
        setCurrentTime(newTime);
      },
      [duration, isDragging],
    );

    const handleProgressClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        const percentage = calculateProgress(e.clientX);
        updateProgress(percentage);
        player.play();
        setPlayerStatus('play');
      },
      [calculateProgress, updateProgress],
    );

    const handleMouseDown = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setIsDragging(true);

        const percentage = calculateProgress(e.clientX);
        updateProgress(percentage);

        const handleMouseMove = (e: MouseEvent) => {
          e.preventDefault();
          const percentage = calculateProgress(e.clientX);
          updateProgress(percentage);
        };

        const handleMouseUp = (e: MouseEvent) => {
          e.preventDefault();
          setIsDragging(false);
          const percentage = calculateProgress(e.clientX);
          const newTime = (duration * percentage) / 100;
          player.currentTime = newTime;
          player.play();
          setPlayerStatus('play');

          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      },
      [calculateProgress, updateProgress, duration],
    );

    // 处理触摸事件
    const handleTouchStart = useCallback(
      (e: React.TouchEvent<HTMLDivElement>) => {
        e.stopPropagation();
        setIsDragging(true);

        const percentage = calculateProgress(e.touches[0].clientX);
        updateProgress(percentage);

        const handleTouchMove = (e: TouchEvent) => {
          e.preventDefault();
          const percentage = calculateProgress(e.touches[0].clientX);
          updateProgress(percentage);
        };

        const handleTouchEnd = (e: TouchEvent) => {
          setIsDragging(false);
          const percentage = calculateProgress(e.changedTouches[0].clientX);
          const newTime = (duration * percentage) / 100;
          player.currentTime = newTime;
          player.play();
          setPlayerStatus('play');

          document.removeEventListener('touchmove', handleTouchMove);
          document.removeEventListener('touchend', handleTouchEnd);
        };

        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleTouchEnd);
      },
      [calculateProgress, updateProgress, duration],
    );

    return (
      <div
        className={`h-[40px] flex justify-center items-center text-app-primary ${className}`}
      >
        <div className="cursor-pointer" onClick={handlePlayPause}>
          {playerStatus === 'pause' ? (
            <PlayIcon className="bg-common-controller-bar-progress-color" />
          ) : (
            <PauseIcon className="bg-common-controller-bar-progress-color" />
          )}
        </div>

        <div className="text-[12px] font-number font-[600] leading-[16px] tracking-[0.5px] pl-[6px] pr-[10px]">
          {calctime(currentTime)}
        </div>

        <div
          ref={progressRef}
          className="relative cursor-pointer flex-1 h-[20px] flex items-center"
          onClick={handleProgressClick}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div className="track absolute w-full bg-app-active rounded-[2px] h-[4px]" />
          <div
            className="progress bg-[#5661F1] absolute left-0 h-[4px] rounded-[2px]"
            style={{ width: `${progressPercentage}%` }}
          >
            <div
              className={`dot absolute w-[12px] h-[12px] bg-app-primary rounded-[50%] right-[-6px] top-[50%] -translate-y-1/2 ${
                isDragging ? 'scale-125' : ''
              } transition-transform duration-200`}
            />
          </div>
        </div>

        <span className="text-[12px] leading-[16px] tracking-[0.5px] pl-[12px]  font-number font-[600]">
          {calctime(duration)}
        </span>

        {videoRef ? (
          <span
            className="pl-[6px] cursor-pointer"
            onClick={() => {
              videoRef?.current?.requestFullscreen();
            }}
          >
            <FullScreenIcon />
          </span>
        ) : null}
      </div>
    );
  },
);

export default ControllerBar;
